import React, { useEffect, useMemo, useState, } from "react";

import Table from "../../components/utils/Table";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import Button from "../../components/utils/Button";
import CardLayout from "../../components/utils/CardLayout";
import Modal from "../../components/utils/Modal";
import ToastAlert from "../../components/utils/ToastAlert";
import { downloadExcelSampleFile, maskEmail } from "../../helpers/commonFunction";
import userActions from "../../redux/user/actions";
import moment from "moment";
import { useLocation } from "react-router-dom";

const { createUser, getAssignableRoles, getUserList, modifyUser, resetMessage, } = userActions;
const dateFormat = process.env.REACT_APP_DATE_TIME_FORMAT;

const User = () => {
    const dispatch = useDispatch();
    const [searchItem, setSearchItem] = useState("");
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showEmail, setShowEmail] = useState(true);
    const [emailInput, setEmailInput] = useState("");
    const [confirmAction, setConfirmAction] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [isEmailEditing, setIsEmailEditing] = useState(false);
    const location = useLocation();
    const searchByDistributorID = location.state?.DistributorID || 0;

    const {
        roleList,
        getRoleLoading,

        userList,
        getUserListLoading,
        getUserListError,

        createUserLoading,
        createUserError,
        createUserMessage,

        modifyUserLoading,
        modifyUserError,
        modifyUserMessage,
    } = useSelector((state) => state.user);

    const { register, handleSubmit, clearErrors, reset, watch, setValue, formState: { errors }, } = useForm({
        mode: "onChange",
        defaultValues: {
            userId: "", roleId: "", password: "", email: "", isActive: true,
        },
    });

    // GENERATE USER ID
    const generateUserId = () => {
        const prefix = "USR";
        const digits = new Uint8Array(12);
        crypto.getRandomValues(digits);
        const randomDigits = Array.from(digits, (value) => value % 10).join("");
        return `${prefix}${randomDigits}`;
    };

    useEffect(() => {
        dispatch(getUserList({ distributorId: Number(searchByDistributorID) || 0 }));
        dispatch(getAssignableRoles());
    }, [dispatch, searchByDistributorID]);

    // CREATE SUCCESS / ERROR
    useEffect(() => {
        if (createUserError) {
            ToastAlert({ type: "error", text: createUserError, });
            dispatch(resetMessage()); return;
        }
        if (createUserMessage) {
            ToastAlert({ type: "success", text: createUserMessage, });
            closeModal();
            dispatch(getUserList({ distributorId: Number(searchByDistributorID) || 0 }));
            dispatch(resetMessage());
        }
    }, [createUserError, createUserMessage]);

    // MODIFY SUCCESS / ERROR
    useEffect(() => {
        if (modifyUserError) {
            ToastAlert({ type: "error", text: modifyUserError, });
            setConfirmAction(null);
            dispatch(resetMessage());
            return;
        }

        if (modifyUserMessage) {
            ToastAlert({ type: "success", text: modifyUserMessage, });
            setConfirmAction(null);
            closeModal();
            dispatch(getUserList({ distributorId: Number(searchByDistributorID) || 0 }));
            dispatch(resetMessage());
        }
    }, [modifyUserError, modifyUserMessage, dispatch,]);

    // USER LIST ERROR
    useEffect(() => {
        if (getUserListError) {
            ToastAlert({ type: "error", text: getUserListError, });
            dispatch(resetMessage());
        }
    }, [getUserListError, dispatch,]);

    // FILTER
    const filteredItems = useMemo(() => {
        if (!Array.isArray(userList)) { return []; }
        if (!searchItem) { return userList; }
        return userList.filter((item) =>
            Object.values(item).some((value) => String(value ?? "").toLowerCase().includes(searchItem.toLowerCase()))
        );
    }, [userList, searchItem]);

    // OPEN ADD MODAL
    const openAddModal = () => {
        // const newUserId = generateUserId();
        setEditMode(false);
        setSelectedUser(null);
        reset({
            userId: "",
            roleId: "",
            password: "",
            email: "",
            isActive: true,
        });
        setEmailInput("");
        setShowEmail(true);
        setShowPassword(false);
        setIsOpenModal(true);
    };

    // OPEN EDIT MODAL
    const handleEditUser = (item) => {
        setEditMode(true);
        setIsEmailEditing(false)
        setSelectedUser(item);
        setEmailInput(item?.EmailID);
        reset({
            userId: item?.Id ?? "",
            roleId: "",
            password: "",
            // email: item?.EmailID ?? "",
            isActive: item?.IsActive ?? false,
        });
        setShowEmail(false);
        setShowPassword(false);
        setIsOpenModal(true);
    };

    // CLOSE MODAL
    const closeModal = () => {
        setIsOpenModal(false);
        setEditMode(false);
        setSelectedUser(null);
        setIsEmailEditing(false)
        clearErrors()

        setEmailInput("");
        setShowEmail(true);
        setShowPassword(false);
        reset({
            userId: "",
            roleId: "",
            password: "",
            email: "",
            isActive: true,
        });
    };
    // SUBMIT
    const handleUserSubmit = (data) => {

        // EDIT
        if (editMode) {
            const payload = {
                id: selectedUser?.Id,
                email: emailInput || data.email,
                isActive: data.isActive,
            };
            dispatch(modifyUser(payload));
            return;
        } else {
            // CREATE
            const payload = {
                userId: data.userId,
                roleId: data.roleId,
                password: data.password,
                emailId: data.email,
                isActive: data.isActive,
            };
            dispatch(createUser(payload));
        }

    };
    // --------status-change-----------
    const handleStatusChange = (item) => {
        const newStatus = !item?.IsActive;
        setConfirmAction({
            id: item?.Id,
            isActive: newStatus,
        });
    };

    const handleConfirmStatusChange = () => {
        if (!confirmAction?.id) {
            ToastAlert({
                type: "error",
                text: "User ID is not available.",
            });
            return;
        }

        dispatch(modifyUser(confirmAction));
    };
    useEffect(() => {
        if (!confirmAction) return;

        const handleOutsideClick = (event) => {
            const popup = document.querySelector(".dealer-confirm-popup");

            if (popup && !popup.contains(event.target)) {
                setConfirmAction(null);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [confirmAction]);
    // TABLE
    const dataSource = useMemo(() => {
        return filteredItems.map(
            (item, index) => ({
                key: index + 1,
                userId: item?.Id ?? "-",
                email: item?.EmailID ? maskEmail(item?.EmailID) : "-",
                createdDate: item?.CreatedDttm ? moment(item.CreatedDttm).format(dateFormat) : "-",
                modifiedDate: item?.LastModifiedDttm ? moment(item.LastModifiedDttm).format(dateFormat) : "-",
                status: (
                    <span className={`badge ${item?.IsActive ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`} >
                        {item?.IsActive ? "Active" : "Inactive"} </span>
                ),
                action: (<>
                    {/*  <button type="button" className="btn btn-sm btn-outline-primary" title="Edit User" onClick={() => handleEditUser(item)} >
                         <i className="ri-edit-line" />
                     </button> */}
                    <div
                        className="position-relative d-flex gap-2"
                        style={{ width: "fit-content" }}
                    >
                        <button
                            type="button"
                            className={`btn btn-sm rounded-circle ${item?.IsActive ? "bg-danger-subtle text-danger" : "bg-success-subtle text-success "}`}
                            title={item?.IsActive ? "Deactivate" : "Activate"}
                            onClick={() => handleStatusChange(item)}
                            disabled={modifyUserLoading}
                        >
                            <i className={item?.IsActive ? "ri-delete-bin-line" : "ri-checkbox-circle-line"} />
                        </button>

                        {confirmAction?.id === item?.Id && (
                            <div
                                className="dealer-confirm-popup position-absolute bg-white border rounded shadow-sm p-3"
                                style={{
                                    zIndex: 1050,
                                    minWidth: "220px",
                                    right: "100%",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    marginRight: "8px",
                                }}
                            >
                                <h6 className="mb-2 text-center">
                                    Are you sure you want{" "}
                                    <strong>
                                        {confirmAction?.isActive ? "activate" : "deactivate"}
                                    </strong>{" "}
                                    this user?
                                </h6>

                                <div className="d-flex justify-content-center gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-secondary"
                                        onClick={handleConfirmStatusChange}
                                        disabled={modifyUserLoading}
                                    >
                                        {modifyUserLoading ? "..." : "Yes"}
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() => setConfirmAction(null)}
                                        disabled={modifyUserLoading}
                                    >
                                        No
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </>
                ),
            })
        );
    }, [filteredItems, confirmAction, modifyUserLoading]);

    // TABLE COLUMNS
    const columns = [
        { displayName: "Sr.No.", key: "key", },
        { displayName: "Email Address", key: "email", },
        { displayName: "Created Date", key: "createdDate", },
        { displayName: "Modified Date", key: "modifiedDate", },
        { displayName: "Status", key: "status", },
        { displayName: "Action", key: "action", },
    ];

    // EXCEL
    const downloadResponseFile = () => {
        const data =
            filteredItems.map(
                (item, index) => ({
                    "Sr.No.": index + 1,
                    "Email Address": item?.EmailID ? maskEmail(item?.EmailID) : "-",
                    Status: item?.IsActive ? "Active" : "Inactive",
                    "Created Date": item?.CreatedDttm ? moment(item.CreatedDttm).format(dateFormat) : "-",
                    "Modified Date": item?.LastModifiedDttm ? moment(item.LastModifiedDttm).format(dateFormat) : "-",
                })
            );
        downloadExcelSampleFile(data, "user-list.xlsx");
    };

    const handleEmailChange = (e) => {
        const inputValue = e.target.value;
        let newEmail = inputValue;
        // When the input is currently masked,
        // preserve the hidden characters from the real email.
        if (!showEmail) {
            const previousEmail = emailInput;

            const atIndex = previousEmail.indexOf("@");

            if (atIndex !== -1) {
                const previousUsername = previousEmail.substring(0, atIndex);

                const newAtIndex = inputValue.indexOf("@");

                if (newAtIndex !== -1) {
                    const newUsername = inputValue.substring(0, newAtIndex);
                    const newDomain = inputValue.substring(newAtIndex);

                    // Keep original hidden characters
                    const hiddenPart = previousUsername.substring(1);

                    newEmail = newUsername.charAt(0) + hiddenPart + newDomain;
                }
            }
        }

        setEmailInput(newEmail);
        setValue("email", newEmail, {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    const handleEmailFocus = () => {
        setIsEmailEditing(true);
    };

    const handleEmailBlur = () => {
        setIsEmailEditing(false);
        setShowEmail(false)
    };


    const isSubmitting = createUserLoading || modifyUserLoading;
    return (
        <CardLayout
            title="Users"
            icon="fas fa-users"
            rightContent={
                <div className="d-flex flex-wrap align-items-center gap-1">
                    {/* SEARCH */}
                    <input
                        type="search"
                        className="form-control"
                        style={{ width: "300px", }}
                        placeholder="Search..."
                        value={searchItem}
                        onChange={(e) => setSearchItem(e.target.value)}
                    />
                    {/* EXCEL */}
                    <Button
                        title="Excel"
                        buttonType="button"
                        variant="secondary"
                        icon={<i className="ri-download-cloud-line me-2" />}
                        onClick={downloadResponseFile}
                    />
                    {/* ADD USER */}
                    <Button
                        title="Add User"
                        buttonType="button"
                        variant="primary"
                        icon={<i className="ri-add-line me-1" />}
                        onClick={openAddModal}
                    />
                </div>
            }
        >
            <Table
                columns={columns}
                loading={getUserListLoading}
                data={dataSource}
                pagination={true}
                tableHeight="380px"
            />

            {/* ADD / MODIFY USER MODAL */}
            <Modal
                showModal={isOpenModal}
                title={editMode ? "Modify User" : "Add User"}
                onHide={closeModal}
                width={700}
                showFooter={false}
            >
                <form
                    className="row"
                    onSubmit={handleSubmit(handleUserSubmit)}
                >
                    {/* USER ID */}
                    {!editMode && (
                        <div className="form-group col-md-6 mb-3">
                            <label className="form-label fw-semibold">
                                User ID{" "}
                                {/* <small style={{ fontSize: "10px" }}> (Auto-Generated) </small> */}
                                <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                     placeholder="Enter User ID"
                                    // maxLength={20}
                                    {...register("userId", {
                                        required: "User ID is required",
                                        maxLength: {
                                            value: 20,
                                            message: "Maximum 20 characters allowed",
                                        },
                                    })}
                                />
                                {/* <span
                                    className="input-group-text bg-white"
                                    onClick={() => setValue("userId", generateUserId())}
                                    title="Generate User ID"
                                    style={{ cursor: "pointer" }}
                                >
                                    <i className="ri-refresh-line" />
                                </span> */}
                            </div>
                            {errors.userId && (<div className="text-danger small"> {errors.userId.message} </div>)}
                        </div>
                    )}
                    {/* ROLE */}
                    {!editMode && (
                        <div className="form-group col-md-6 mb-3">
                            <label className="form-label fw-semibold">
                                Role{" "}
                                <span className="text-danger">*</span>
                            </label>

                            <select
                                className={`form-select ${errors.roleId ? "is-invalid" : ""}`}
                                {...register("roleId", { required: "Role is required", })}
                                disabled={getRoleLoading}
                            >
                                <option value=""> {getRoleLoading ? "Loading roles..." : "Select Role"} </option>
                                {roleList?.map((role) => (
                                    <option key={role.ID} value={role.ID} >
                                        {role.RoleName}
                                    </option>
                                ))}
                            </select>
                            {errors.roleId && (<div className="invalid-feedback"> {errors.roleId.message} </div>)}
                        </div>
                    )}
                    {/* EMAIL */}
                    <div className="form-group col-md-6 mb-3">
                        <label className="form-label fw-semibold">
                            Email Address{" "} <span className="text-danger"> * </span>
                        </label>

                        <div className="input-group position-relative">
                            <input
                                type="text"
                                placeholder="Enter email address"
                                className={`form-control ${errors.email ? "is-invalid" : ""
                                    }`}
                                value={showEmail || isEmailEditing ? emailInput : maskEmail(emailInput)}
                                {...register("email", {
                                    required: "Email address is required",
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: "Please enter a valid email address",
                                    },
                                })}
                                onFocus={handleEmailFocus}
                                onBlur={handleEmailBlur}
                                onChange={handleEmailChange}
                            />


                            <button
                                type="button"
                                className="input-group-text"
                                onClick={() => {
                                    setShowEmail(pre => !pre);
                                }}
                                style={{
                                    cursor: "pointer",
                                    backgroundColor: "var(--bg-content)",
                                    borderColor: "var(--border-color)",
                                    color: "var(--hedging-text-color)",
                                    minWidth: "42px",
                                    justifyContent: "center",
                                }}
                                title={showEmail ? "Hide Email" : "Show Email"}
                            >
                                <i
                                    className={showEmail ? "ri-eye-off-line" : "ri-eye-line"}
                                    style={{ fontSize: "18px" }}
                                />
                            </button>
                        </div>


                        {errors.email && (
                            <div className="invalid-feedback d-block">
                                {errors.email.message}
                            </div>
                        )}
                    </div>

                    {/* PASSWORD */}
                    {!editMode && (
                        <div className="form-group col-md-6 mb-3">
                            <label className="form-label fw-semibold">
                                Password{" "}
                                <span className="text-danger">*</span>
                                <span
                                    className="position-relative ms-2 info-icon-wrapper"
                                    style={{ cursor: "pointer" }}
                                >
                                    <i className="fas fa-info-circle text-primary fs-5 info-icon-text" />
                                    <div className="password-info-popup position-absolute bg-white border rounded shadow-sm py-2">
                                        <ul className="mb-0 ps-4 custom-info-list">
                                            <li className="mb-1"> Minimum characters 8 </li>
                                            <li className="mb-1"> One uppercase character </li>
                                            <li className="mb-1"> One lowercase character </li>
                                            <li className="mb-1"> One special character </li>
                                            <li className="mb-1"> One number </li>
                                            <li className="mb-1"> e.g. Example@123 </li>
                                        </ul>
                                    </div>
                                </span>
                            </label>

                            <div className="input-group">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Enter password"
                                    className={`form-control ${errors.password ? "is-invalid" : ""}`}
                                    {...register("password", {
                                        required:
                                            "Password is required",
                                        minLength: { value: 8, message: "Password must be at least 8 characters", },
                                        pattern: { value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/, message: "Use uppercase, lowercase, number & special character.", },
                                    })}
                                />

                                <button
                                    type="button"
                                    className="input-group-text"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    style={{ cursor: "pointer" }} >
                                    <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"} />
                                </button>
                            </div>

                            {errors.password && (
                                <div className="text-danger small">
                                    {errors.password.message}
                                </div>
                            )}
                        </div>
                    )}
                    {/* STATUS */}

                    <div className="form-group col-md-6 mb-3">
                        <label className="form-label fw-semibold">
                            Status
                        </label>

                        <div className="d-flex align-items-center gap-2">
                            <div className="form-check form-switch m-0">
                                <input className="form-check-input" type="checkbox" role="switch" {...register("isActive")} />
                            </div>

                            <span className="fw-semibold"> {watch("isActive") ? "Active" : "Inactive"} </span>
                        </div>
                    </div>

                    {/* BUTTONS */}

                    <div className="col-md-12 d-flex justify-content-end gap-3 mt-3">
                        <Button title="Close" buttonType="button" variant="secondary" onClick={closeModal} icon={<i className="ri-close-circle-line me-1" />} />
                        <Button
                            title={isSubmitting ? editMode ? "Updating..." : "Submitting..." : editMode ? "Update" : "Submit"}
                            buttonType="submit" variant="primary" disabled={isSubmitting}
                            icon={<i className="ri-send-plane-fill me-1" />}
                        />
                    </div>
                </form>
            </Modal>
        </CardLayout>
    );
};

export default User;